import React, { useState } from 'react';
import styles from './Register.module.scss';
import { TextInput } from '@/components/common/TextInput/TextInput';
import { useUser } from '@/hooks';
import { validateUtils } from '@/utils/helpers/validation';
import { ERROR_CODES_FIREBASE } from '@/constants/integrations/firebase';
import { INPUT_TYPE } from '@/constants/ui/input';
import { t } from 'i18next';
import {
  Button,
  EButtonHierarchy,
  EButtonSize,
} from '@/components/common/Button/Button';

interface IRegisterProps {
  onRegisterSuccess: () => void;
  source: string;
}
export const Register: React.FC<IRegisterProps> = (props: IRegisterProps) => {
  const userHook = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registrationError, setRegistrationError] = useState('');

  const allFieldsAreShown = () => showPassword;

  const checkValidation = (): boolean => {
    setEmailError('');
    setPasswordError('');
    if (!email) {
      setEmailError(t('validation.email.required'));
      return false;
    }
    if (!validateUtils.validateEmail(email)) {
      setEmailError(t('validation.email.invalid'));
      return false;
    }
    if (showPassword) {
      if (!password) {
        setPasswordError(t('validation.password.required'));
        return false;
      }
      if (!validateUtils.validatePassword(password)) {
        setPasswordError(t('LoginErrorMessage.passwordMin'));
        return false;
      }
    }
    return true;
  };

  const handleFieldsView = () => {
    if (!showPassword && validateUtils.validateEmail(email)) {
      setShowPassword(true);
    }
  };

  const handleContinue = async () => {
    handleFieldsView();
    if (!checkValidation()) {
      return;
    }
    if (!allFieldsAreShown()) {
      return;
    }
    try {
      const displayName = userHook.getDisplayNameFromEmail(email);
      await userHook.register(email, password, displayName, props.source);
      props.onRegisterSuccess();
    } catch (e: any) {
      if (e.code === ERROR_CODES_FIREBASE.EMAIL_IN_USE) {
        setEmailError(t('validation.email.inUse'));
      } else {
        setRegistrationError(t('validation.errorOccurredWhileRegistering'));
      }
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError && validateUtils.validateEmail(value)) {
      setEmailError('');
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError && validateUtils.validatePassword(value)) {
      setPasswordError('');
    }
  };

  return (
    <div className={styles.loginEmailPassword}>
      <div className={styles.formWrapper}>
        <TextInput
          type={INPUT_TYPE.EMAIL}
          placeholder={t('register.placeholders.email')}
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          onEnter={handleContinue}
          focusOnMount={true}
        />
        {showPassword && (
          <TextInput
            type={INPUT_TYPE.PASSWORD}
            placeholder={t('register.placeholders.password')}
            value={password}
            onChange={handlePasswordChange}
            error={passwordError}
            onEnter={handleContinue}
            focusOnMount={true}
            showPasswordToggle={true}
          />
        )}
        {registrationError && <p>{registrationError}</p>}
      </div>
      <Button
        fillWidth={true}
        size={EButtonSize.md}
        hierarchy={EButtonHierarchy.primary}
        onClick={handleContinue}
        text={
          allFieldsAreShown() ? 'buttons.createAccount' : 'buttons.continue'
        }
      />
    </div>
  );
};
